import {generateSSOUrl} from '@github-ui/global-sso-banner/generate-sso-url'
import {Icon} from '@github-ui/pacer/Icon'
import {useLayoutContext} from '@github-ui/pacer/Layout/layout-context'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useLocation} from '@github-ui/react-router'
import {ShieldLockIcon, SidebarCollapseIcon, SidebarExpandIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Tooltip, type TooltipProps} from '@primer/react'
import {clsx} from 'clsx'
import {type ReactNode, useEffect, useId} from 'react'

import {useChatStateValues} from '../../utils/CopilotChatContext'
import {useChatManager} from '../../utils/CopilotChatManagerContext'
import {ConversationList} from '../conversation/ConversationListV2'
import {ActivityPanel} from './ActivityPanel'
import styles from './CopilotNavigation.module.css'
import {OrgsFailedToLoadBanner} from './OrgsFailedToLoadBanner'
import {ServiceList} from './ServiceList'
import {UserMenu} from './UserMenu'

export function CopilotNavigationWithContext() {
  const {sidebarExpanded, sidebarVisible, layoutBreakpoint, toggleSidebar} = useLayoutContext()
  return (
    <CopilotNavigation
      sidebarExpanded={sidebarExpanded}
      sidebarVisible={sidebarVisible}
      sidebarFloating={layoutBreakpoint}
      onToggleSidebar={toggleSidebar}
    />
  )
}

interface CopilotNavigationProps {
  onSidebarClick?: () => void
  onToggleSidebar: () => void
  sidebarExpanded: boolean
  sidebarFloating: boolean
  sidebarVisible: boolean
}

interface ConditionalTooltipProps {
  children: ReactNode
  showTooltip: boolean
  text: string
  direction?: TooltipProps['direction']
}

function ConditionalTooltip({children, showTooltip, text, direction = 'e'}: ConditionalTooltipProps) {
  if (!showTooltip) {
    return <>{children}</>
  }
  return (
    <Tooltip text={text} direction={direction}>
      {children}
    </Tooltip>
  )
}

export function CopilotNavigation({
  onSidebarClick,
  onToggleSidebar: toggleSidebar,
  sidebarExpanded,
  sidebarFloating: sidebarFloating,
  sidebarVisible,
}: CopilotNavigationProps) {
  const isCondensed = sidebarFloating ? false : !sidebarExpanded

  const manager = useChatManager()
  const {threadsLoading, threads, selectedThreadID, ssoOrganizations, agentsEnabled, orgsFailedToLoad} =
    useChatStateValues(
      'threadsLoading',
      'threads',
      'selectedThreadID',
      'ssoOrganizations',
      'agentsEnabled',
      'orgsFailedToLoad',
    )

  const location = useLocation()
  const isSharedThread = location.pathname.includes('/share/')

  useEffect(() => {
    if (threads.size === 0 && threadsLoading.state === 'pending') {
      void manager.fetchThreads()
    }
  }, [threads.size, manager, threadsLoading.state])

  const handleSidebarClick = () => {
    onSidebarClick?.()
    if (sidebarVisible) {
      toggleSidebar()
    }
  }

  const id = useId()

  const renderToggleButton = () => {
    return (
      <ConditionalTooltip showTooltip={isCondensed} text="Expand sidebar">
        <button
          className={styles.footerButton}
          aria-label={isCondensed ? 'Expand sidebar' : 'Collapse sidebar'}
          onClick={toggleSidebar}
        >
          {isCondensed ? (
            <SidebarCollapseIcon size={16} className={styles.toggleButtonIcon} />
          ) : (
            <SidebarExpandIcon size={16} className={styles.toggleButtonIcon} />
          )}
          <span className={clsx(styles.footerButtonContent, isCondensed && styles.condensed)}>Collapse</span>
        </button>
      </ConditionalTooltip>
    )
  }

  const renderSSOMenu = () => {
    if (ssoOrganizations.length === 0) return null

    if (ssoOrganizations.length === 1) {
      const org = ssoOrganizations[0]
      if (!org) return null
      return (
        <ConditionalTooltip showTooltip={isCondensed} text="Single sign-on">
          <a href={generateSSOUrl(org.login, location)} className={styles.footerButton}>
            <ShieldLockIcon size={16} className={styles.footerButtonIcon} />
            <span
              className={clsx(styles.footerButtonContent, isCondensed && styles.condensed)}
              aria-hidden={isCondensed}
            >
              Single sign-on
            </span>
          </a>
        </ConditionalTooltip>
      )
    }

    return (
      <ConditionalTooltip showTooltip={isCondensed} text="Single sign-on">
        {/* Wrapper div to enable Tooltip on ActionMenu */}
        <div>
          <ActionMenu>
            <ActionMenu.Anchor>
              <button className={styles.footerButton} aria-label="Single sign-on">
                <Icon icon={ShieldLockIcon} size={16} className={styles.footerButtonIcon} />
                <div
                  className={clsx(styles.footerButtonContent, isCondensed && styles.condensed)}
                  aria-hidden={isCondensed}
                >
                  Single sign-on
                </div>
              </button>
            </ActionMenu.Anchor>
            <ActionMenu.Overlay>
              <ActionList>
                {ssoOrganizations.map(org => (
                  <ActionList.LinkItem key={org.login} href={generateSSOUrl(org.login, location)}>
                    {org.login}
                  </ActionList.LinkItem>
                ))}
              </ActionList>
            </ActionMenu.Overlay>
          </ActionMenu>
        </div>
      </ConditionalTooltip>
    )
  }

  return (
    <>
      <ErrorBoundary fallback={<SidebarContentsFallback />}>
        <nav aria-labelledby={`${id}-quick-links`} className={styles.quicklinks}>
          <h3 id={`${id}-quick-links`} className="sr-only">
            Quick links
          </h3>
          <ServiceList isSidebarCondensed={isCondensed} toggleFloatingSidebar={handleSidebarClick} />
        </nav>
        {agentsEnabled ? (
          <ActivityPanel
            handleSidebarClick={handleSidebarClick}
            isCondensed={isCondensed}
            onToggleSidebar={toggleSidebar}
          />
        ) : (
          <>
            {!isCondensed && orgsFailedToLoad && <OrgsFailedToLoadBanner />}
            <ConversationList
              selectedItemID={isSharedThread ? null : selectedThreadID}
              loadingState={threadsLoading.state}
              onConversationSelect={handleSidebarClick}
              isSidebarCondensed={isCondensed}
            />
          </>
        )}
      </ErrorBoundary>

      <div className={styles.footer}>
        {!sidebarFloating && renderToggleButton()}
        {renderSSOMenu()}
        <UserMenu isCondensed={isCondensed} />
      </div>
    </>
  )
}

function SidebarContentsFallback() {
  return (
    <div className={styles.blankslateContainer}>
      <div>Error loading conversations</div>
    </div>
  )
}
